import { useMemo, useState } from "react";

import { gql } from "@apollo/client";
import { Callout, Classes, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";

import { pluralize } from "@/components/helpers/string-utils";
import { Box } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { CreditPriceOption } from "@/components/pages_logged_in/services/CreditPriceOption";
import { CreditPriceSelect } from "@/components/pages_logged_in/services/CreditPriceSelect";
import { PriceUpdateConfirmationDialog } from "@/components/pages_logged_in/services/PriceUpdateConfimationDialog";
import {
  generateAllPriceOptions,
  PriceUpdateConfirmationStep,
} from "@/components/pages_logged_in/services/utils";
import { WithLoader } from "@/components/pieces/loading/WithLoader";
import { getUser } from "@/components/session/JwtTokenManager";
import { SITE_NAME, SUBTEXT_COLOR } from "@/css/constants";
import {
  ProductDetailQuery,
  SetProductPriceMutationVariables,
  useSetProductPriceMutation,
} from "@/graphql";
import { CreditPriceOption as ICreditPriceOption } from "@/types/products";

const MAX_DISPLAYED_OPTIONS = 3;

const CreditPriceOptionsBox = styled(Box)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 20px;
  row-gap: 10px;
  margin-top: 12px;
  @media screen and (min-width: ${breakpoints.lg}px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const LOADER = (
  <Box
    className={Classes.SKELETON}
    height={64}
    width="100%"
    css={{ marginTop: 12 }}
  />
);

export const SET_PRODUCT_PRICE_MUTATION = gql`
mutation SetProductPrice(
  $productId: ID!
  $price: Decimal!
  $legacyCustomerIds: [ID]
) {
  setProductPrice(
    productId: $productId
    price: $price
    legacyCustomerIds: $legacyCustomerIds
  ) {
    ok
  }
}
`;

interface PriceSettingsProps {
  coachProduct?: ProductDetailQuery["productById"]["coachProduct"];
  product?: ProductDetailQuery["productById"]["product"];
  loading: boolean;
}

export function PriceSettings({
  coachProduct,
  product,
  loading,
}: PriceSettingsProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState(
    PriceUpdateConfirmationStep.INITIAL,
  );

  const [setCoachProductPrice, { loading: isSettingProductPrice }] =
    useSetProductPriceMutation({
      onCompleted: () => {
        setDialogStep(PriceUpdateConfirmationStep.SUCCESS);
      },
      onError: () => {
        setDialogStep(PriceUpdateConfirmationStep.ERROR);
      },
      refetchQueries: ["ProductDetail", "CoachCustomers"],
    });

  const [newPriceOption, setNewPriceOption] = useState<ICreditPriceOption>();

  const { displayedOptions, hiddenOptions } = useMemo(() => {
    if (!coachProduct) {
      return {
        displayedOptions: [],
        hiddenOptions: [],
      };
    }
    const allPriceOptions = generateAllPriceOptions(
      coachProduct,
      coachProduct?.coachPrice,
    );

    const displayedItems = allPriceOptions.slice(0, MAX_DISPLAYED_OPTIONS);
    const hiddenItems = allPriceOptions.slice(MAX_DISPLAYED_OPTIONS);

    if (hiddenItems.length === 1) {
      const item = hiddenItems.pop();
      if (item) {
        displayedItems.push(item);
      }
    }

    return {
      displayedOptions: displayedItems,
      hiddenOptions: hiddenItems,
    };
  }, [coachProduct]);

  const currentUser = getUser();
  const coachId = currentUser?.id;
  const currentPriceValue = coachProduct?.coachPrice;
  const coachCreditValue = coachProduct?.coachCreditValue;
  const enableRfq = product?.enableRfq;

  const requestToUpdateCoachProductPrice = (option: ICreditPriceOption) => {
    if (option.value !== currentPriceValue) {
      setDialogStep(PriceUpdateConfirmationStep.INITIAL);
      setNewPriceOption(option);
      setIsDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handlePriceUpdateConfirmed = (
    variables: SetProductPriceMutationVariables,
  ) => {
    setCoachProductPrice({ variables }).then(() => {
      const key = `legacyPricingClientsByCoach_${coachId}_v2`;
      const lastLegacyPricing = {
        clientIds: variables.legacyCustomerIds || [],
      };
      window.localStorage.setItem(key, JSON.stringify(lastLegacyPricing));
    });
  };

  return (
    <Box>
      <Box fontSize={20} fontWeight={600}>
        Price Settings
      </Box>
      <Box fontSize={16} color={SUBTEXT_COLOR}>
        How many credits per session do you charge for this service? To qualify
        for locked pricing tiers, please contact your {SITE_NAME} rep.
      </Box>

      <WithLoader loading={loading} loader={LOADER}>
        {enableRfq ? (
          <Box mt={3}>
            <Callout intent="primary">
              Pricing for this service is handling via quoting
            </Callout>
          </Box>
        ) : (
          <CreditPriceOptionsBox>
            {displayedOptions.map((option) => (
              <Box key={option.value}>
                <CreditPriceOption
                  price={option.value}
                  isLocked={option.isLocked}
                  onClick={() => {
                    !option.isLocked &&
                      requestToUpdateCoachProductPrice(option);
                  }}
                  isSelected={currentPriceValue === option.value}
                />
              </Box>
            ))}
            {!!hiddenOptions.length && (
              <Box>
                <CreditPriceSelect
                  options={hiddenOptions}
                  value={hiddenOptions.find(
                    (option) => option.value === currentPriceValue,
                  )}
                  onChange={requestToUpdateCoachProductPrice}
                />
              </Box>
            )}
          </CreditPriceOptionsBox>
        )}
        {coachCreditValue && (
          <Box mt={3}>
            <Callout
              intent="primary"
              icon={<Icon icon="lightning" color="#ffd443" />}
            >
              <Box fontWeight={700} fontSize={16}>
                You earn a bonus on this service!
              </Box>
              The client pays{" "}
              <strong>
                {pluralize({
                  amount: currentPriceValue,
                  singular: "credit",
                  plural: "credits",
                })}
              </strong>
              , but you'll earn{" "}
              <strong>
                {pluralize({
                  amount: coachCreditValue,
                  singular: "credit",
                  plural: "credits",
                })}
              </strong>{" "}
              with this bonus. We've manually overridden the system to configure
              it. If you change your prices, the bonus will be removed.`
            </Callout>
          </Box>
        )}
      </WithLoader>

      {coachProduct && product && (
        <PriceUpdateConfirmationDialog
          isOpen={isDialogOpen}
          isLoading={isSettingProductPrice}
          step={dialogStep}
          coachProduct={coachProduct}
          product={product}
          newPriceOption={newPriceOption}
          onClose={handleDialogClose}
          onConfirm={handlePriceUpdateConfirmed}
        />
      )}
    </Box>
  );
}
